import { Injectable, signal, inject } from '@angular/core';
import { BreakpointObserver } from '@angular/cdk/layout';
import { toSignal } from '@angular/core/rxjs-interop';
import { map } from 'rxjs/operators';
import { getUserRoleFromBackendRoleId, UserRole } from '../../features/claim/models/scenario.enum';

const STORAGE_PREFIX = 'gcms:';
const KEY_ROLE = `${STORAGE_PREFIX}selectedRole`;
const KEY_REGION = `${STORAGE_PREFIX}selectedRegion`;
const KEY_COUNTRY = `${STORAGE_PREFIX}selectedCountry`;
const KEY_DM_SITE = `${STORAGE_PREFIX}selectedDmSite`;

@Injectable({
  providedIn: 'root',
})
export class LayoutService {
  private breakpointObserver = inject(BreakpointObserver);

  mobileMenuOpen = signal(false);
  selectedRole = signal<string | number | null>(null);
  selectedRegion = signal<string | number | null>(null);
  selectedCountry = signal<string | number | null>(null);
  selectedDmSite = signal<string | number | null>(null);

  constructor() {
    this.selectedRole.set(this._readStoredValue(KEY_ROLE));
    this.selectedRegion.set(this._readStoredValue(KEY_REGION));
    this.selectedCountry.set(this._readStoredValue(KEY_COUNTRY));
    this.selectedDmSite.set(this._readStoredValue(KEY_DM_SITE));
  }

  /** True when viewport is 1024px or larger - matches Tailwind's lg breakpoint */
  isDesktop = toSignal(
    this.breakpointObserver.observe('(min-width: 1024px)').pipe(map(({ matches }) => matches)),
    { initialValue: typeof window !== 'undefined' ? window.innerWidth >= 1024 : false }
  );

  toggleMobileMenu(): void {
    this.mobileMenuOpen.update(val => !val);
  }

  closeMobileMenu(): void {
    this.mobileMenuOpen.set(false);
  }

  setSelectedRole(value: string | number | null): void {
    this.selectedRole.set(value);
    this._writeOrRemove(KEY_ROLE, value);
  }

  setSelectedRegion(value: string | number | null): void {
    this.selectedRegion.set(value);
    this._writeOrRemove(KEY_REGION, value);
  }

  setSelectedCountry(value: string | number | null): void {
    this.selectedCountry.set(value);
    this._writeOrRemove(KEY_COUNTRY, value);
  }

  setSelectedDmSite(value: string | number | null): void {
    this.selectedDmSite.set(value);
    this._writeOrRemove(KEY_DM_SITE, value);
  }

  private _writeOrRemove(key: string, value: string | number | null): void {
    try {
      if (value == null) localStorage.removeItem(key);
      else localStorage.setItem(key, String(value));
    } catch { /* ignore */ }
  }

  private _readStoredValue(key: string): string | number | null {
    try {
      const raw = localStorage.getItem(key);
      if (raw === null) return null;
      const num = Number(raw);
      return !isNaN(num) && raw.trim() !== '' ? num : raw;
    } catch {
      return null;
    }
  }

  getViewerRole(): UserRole {
    const role = getUserRoleFromBackendRoleId(this.selectedRole());
    return role ?? UserRole.DM_SALES;
  }
}
